import cn from 'clsx';
import * as PropTypes from 'prop-types';
import React, { Component } from 'react';
import { type IOrder, type IItem } from '@wix/cashier-core/dist/src/services/Order/types';
import { ChevronDownLargeSmall } from '@wix/wix-ui-icons-common';
import { getFormattedPrice } from '../../utils/getFormattedPrice';
import { isEventKey, keyCodes } from '../../utils/key-codes';
import { TabIndex } from '../../utils/tabIndexes';
import { t } from '../../utils/i18n';
import s from './OrderSummaryPopover.scss';

interface IOrderSummaryPopoverProps {
  order: IOrder;
  dateNumberFormat: string;
  onShown(): void;
}

export class OrderSummaryPopover extends Component<IOrderSummaryPopoverProps> {
  static contextTypes = {
    deviceType: PropTypes.string,
  };

  private itemsNode;
  private tooltipWrapperNode;

  state = {
    showTooltip: false,
  };

  componentDidMount() {
    this.addClickListener();
  }

  componentWillUnmount() {
    this.removeClickListener();
  }

  addClickListener = () => {
    window.addEventListener('click', this.hideOnClickOutside, false);
  };

  removeClickListener = () => {
    window.removeEventListener('click', this.hideOnClickOutside, false);
  };

  hideOnClickOutside = (e) => {
    if (!this.tooltipWrapperNode.contains(e.target)) {
      this.setState({ showTooltip: false });
    }
  };

  setItemsNode(node) {
    this.itemsNode = node;
    this.setItemsHeight();
  }

  setItemsHeight() {
    // @ts-expect-error
    const isMobile = this.context.deviceType === 'mobile';
    const visibleItemsCount = isMobile ? 4 : 3;
    const itemsLength = this.props.order.description.items.length;
    if (itemsLength <= visibleItemsCount || !this.itemsNode) {
      return;
    }

    const hiddenItemNodes = Array.prototype.slice.call(
      this.itemsNode.querySelectorAll('[data-hook="wcn-order-summary-item"]'),
      visibleItemsCount,
    );

    hiddenItemNodes.forEach((node) => (node.style.display = 'none'));
    this.itemsNode.style.height = `${this.itemsNode.offsetHeight}px`;
    hiddenItemNodes.forEach((node) => (node.style.display = 'block'));
  }

  toggleTooltip = () => {
    if (!this.state.showTooltip && this.props.onShown) {
      this.props.onShown();
    }
    this.setState({ showTooltip: !this.state.showTooltip });
  };

  onKeyDown = (e) => {
    if (!this.state.showTooltip) {
      if (isEventKey(e, keyCodes.arrowDown)) {
        this.setState({ showTooltip: true });
      }
      return;
    }

    if (isEventKey(e, keyCodes.escape)) {
      this.setState({ showTooltip: false });
    }
  };

  onBlur = () => {
    setTimeout(() => {
      if (!this.tooltipWrapperNode.contains(document.activeElement)) {
        this.setState({ showTooltip: false });
      }
    }, 0);
  };

  render() {
    const { order, dateNumberFormat } = this.props;
    // @ts-expect-error
    const isMobile = this.context.deviceType === 'mobile';
    const amount = getFormattedPrice({
      locale: dateNumberFormat,
      price: order.description.amount,
      currency: order.description.currency,
    });

    const toggleButtonProps = {
      className: s.tooltipButton,
      'data-hook': 'wcn-order-summary',
      type: 'button',
      role: 'button',
      'aria-pressed': this.state.showTooltip,
      'data-describedby': 'order-summary-tooltip',
      'aria-label': t('payments.app.accessibility.orderSummary.button', {
        amount,
      }),
      onClick: this.toggleTooltip,
      onKeyPress: this.toggleTooltip,
      onBlur: this.onBlur,
      tabIndex: TabIndex.SummaryPopover,
    };

    if (this.state.showTooltip) {
      toggleButtonProps['aria-labelledby'] = toggleButtonProps['data-describedby'];
    }

    return (
      <div
        ref={(node) => (this.tooltipWrapperNode = node)}
        className={cn(s.tooltipWrapper, isMobile && s.mobile)}
        tabIndex={TabIndex.None}
      >
        <span {...toggleButtonProps}>
          <span>{amount}</span>
          <ChevronDownLargeSmall />
        </span>
        {this.state.showTooltip && isMobile && (
          <div className={s.overlay} data-hook="wcn-order-summary-overlay" onClick={this.toggleTooltip} />
        )}
        {this.state.showTooltip && this.renderTooltipContent()}
      </div>
    );
  }

  renderTooltipContent() {
    const { order, dateNumberFormat } = this.props;
    const { items, amount, currency } = order.description;
    return (
      <div
        id="order-summary-tooltip"
        data-hook="wcn-order-summary-tooltip"
        aria-hidden={!this.state.showTooltip}
        className={s.tooltip}
        aria-live="polite"
        role="tooltip"
      >
        <div
          ref={(node) => this.setItemsNode(node)}
          role="list"
          data-hook="wcn-order-summary-items"
          className={s.items}
        >
          {items.map((item) => this.renderItem(item))}
        </div>
        {items.length > 1 ? (
          <div className={s.total} tabIndex={0}>
            <div className={s.columns}>
              <div className={s.totalLabel}>{t('payments.app.orderSummary.total')}</div>
              <div className={s.totalValue}>
                {getFormattedPrice({
                  locale: dateNumberFormat,
                  price: amount,
                  currency,
                })}
              </div>
            </div>
          </div>
        ) : null}
        <div className={s.arrow} />
      </div>
    );
  }

  renderItem(item: IItem) {
    const { order, dateNumberFormat } = this.props;
    return (
      <div
        key={`wcn-order-summary-item-${item.id}`}
        data-hook="wcn-order-summary-item"
        className={s.item}
        role="listitem"
        tabIndex={0}
      >
        <div className={s.columns}>
          <div className={s.itemName}>{item.name}</div>
          <div className={s.itemPrice}>
            {getFormattedPrice({
              locale: dateNumberFormat,
              price: item.price,
              currency: order.description.currency,
            })}
          </div>
        </div>
        <div className={s.itemQuantity}>
          {t('payments.app.orderSummary.item.quantity', {
            quantity: item.quantity,
          })}
        </div>
      </div>
    );
  }
}
